import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  X,
  ArrowRight,
  ArrowLeft,
  Check,
  ExternalLink,
  Users,
  Trash2,
  UserPlus,
  Loader2,
  AlertTriangle,
  FileText,
  BookUser,
  CalendarCheck,
  CalendarClock,
  XCircle,
  PenLine,
  RotateCcw,
  Ban,
} from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { toast } from 'sonner'
import type { WireArticle, WireBlockReason } from '../../api/types'
import { useWorkflowAction, useBlockArticle, useAdminArticle } from '../../api/hooks'
import { useWireAuth } from '../../auth/useWireAuth'
import { useAuthz } from '../../auth/useAuthz'
import { getCardShelfWorkflowActions, getWorkflowActionAccess } from '../../workflow/actionAccess'

// ─── Stage badge ──────────────────────────────────────────────────────────────

const STAGE_META: Record<string, { label: string; bgVar: string; fgVar: string; dotClass: string }> = {
  qc_review:           { label: 'QC Review',  bgVar: '--amber-3',  fgVar: '--amber-11',  dotClass: 'bg-yellow-400' },
  layout_review:       { label: 'Layout',     bgVar: '--blue-3',   fgVar: '--blue-11',   dotClass: 'bg-blue-400' },
  editorial_approval:  { label: 'Editorial',  bgVar: '--violet-3', fgVar: '--violet-11', dotClass: 'bg-violet-400' },
  scheduled:           { label: 'Scheduled',  bgVar: '--teal-3',   fgVar: '--teal-11',   dotClass: 'bg-cyan-400' },
  post_pub_review:     { label: 'Post-Pub',   bgVar: '--orange-3', fgVar: '--orange-11', dotClass: 'bg-orange-400' },
}

function StageBadge({ stage }: { stage: string }) {
  const m = STAGE_META[stage] ?? { label: stage, bgVar: '--gray-3', fgVar: '--gray-11', dotClass: 'bg-gray-400' }
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: `var(${m.bgVar})`, color: `var(${m.fgVar})` }}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${m.dotClass}`} aria-hidden="true" />
      {m.label}
    </span>
  )
}

// ─── Sign-off row ─────────────────────────────────────────────────────────────

function SignoffRow({
  label,
  accentVar,
  signoff,
}: {
  label: string
  accentVar: string
  signoff?: { by: string; byName?: string; at: string }
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: `var(${accentVar})` }} aria-hidden="true" />
        <span className="text-[11px] text-fg">{label}</span>
      </div>
      {signoff ? (
        <div className="flex items-center gap-1.5">
          <Check size={11} style={{ color: 'var(--jade-11)' }} aria-hidden="true" />
          <span className="text-[10px] text-fg-secondary">
            {signoff.byName ?? signoff.by}
          </span>
        </div>
      ) : (
        <span className="text-[10px]" style={{ color: 'var(--gray-10)' }}>Pending</span>
      )}
    </div>
  )
}

// ─── CardShelf ────────────────────────────────────────────────────────────────

export type CardShelfProps = {
  article: WireArticle | null
  currentUserSub: string
  onClose: () => void
  onAssignRequest: (a: WireArticle) => void
  onDeleteRequest: (a: WireArticle) => void
  onReassignToMe: (a: WireArticle) => void
}

export function CardShelf({
  article,
  currentUserSub,
  onClose,
  onAssignRequest,
  onDeleteRequest,
  onReassignToMe,
}: CardShelfProps) {
  const { user } = useWireAuth()
  const { isEditorialManager, isPostProduction } = useAuthz()
  const canIssueRevision = isEditorialManager || isPostProduction
  const workflowAction = useWorkflowAction()
  const blockArticle = useBlockArticle()
  const { data: fullArticle } = useAdminArticle(article?.id ?? '')
  const open = article !== null

  const CLASSIFIED_BLOCK_TYPES = new Set(['paragraph', 'heading', 'list', 'warning', 'textbox', 'table', 'visual'])
  const hasUnclassifiedBlocks = (fullArticle?.blocks ?? []).some(
    (b) => CLASSIFIED_BLOCK_TYPES.has(b.type) && !b.classification
  )

  // Close on Escape
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  const cycle = article?.workflow.cycles?.[article.workflow.currentCycle - 1]
  const stage = article?.workflow.stage
  const access = article ? getWorkflowActionAccess(article, currentUserSub) : null
  const actions = article ? new Set(getCardShelfWorkflowActions(article, currentUserSub)) : new Set()
  const hasWorkflowActions = actions.size > 0

  const isQcHolder = access?.isQcHolder ?? false
  const isLayoutHolder = access?.isLayoutHolder ?? false
  const isEditorialHolder = access?.isEditorialHolder ?? false
  const qcSigned = access?.qcSigned ?? false
  const layoutSigned = access?.layoutSigned ?? false
  const editorialApproved = access?.editorialApproved ?? false

  const alreadyMine = article && [
    article.workflow.qcAssignedTo,
    article.workflow.layoutAssignedTo,
    article.workflow.editorialAssignedTo,
    article.workflow.postPubAssignedTo,
  ].includes(currentUserSub)

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectNote, setRejectNote] = useState('')
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  const [scheduleDate, setScheduleDate] = useState('')
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false)
  const [rescheduleDate, setRescheduleDate] = useState('')
  const [unscheduleDialogOpen, setUnscheduleDialogOpen] = useState(false)
  const [unscheduleNote, setUnscheduleNote] = useState('')
  const [postPubApproveDialogOpen, setPostPubApproveDialogOpen] = useState(false)
  const [postPubRevisionDialogOpen, setPostPubRevisionDialogOpen] = useState(false)
  const [postPubRevisionNote, setPostPubRevisionNote] = useState('')
  const [postPubRecallDialogOpen, setPostPubRecallDialogOpen] = useState(false)
  const [postPubRecallNote, setPostPubRecallNote] = useState('')
  const [postPubRecallType, setPostPubRecallType] = useState<'substantive-recall' | 'administrative-recall' | 'compliance-recall'>('substantive-recall')
  const [blockDialogOpen, setBlockDialogOpen] = useState(false)
  const [blockReason, setBlockReason] = useState<WireBlockReason>('waiting_for_author')
  const [blockAdditionalInfo, setBlockAdditionalInfo] = useState('')
  const [unblockDialogOpen, setUnblockDialogOpen] = useState(false)

  // Build a default datetime string (next whole hour, local time) for the date input
  function defaultScheduleDatetime() {
    const d = new Date()
    d.setMinutes(0, 0, 0)
    d.setHours(d.getHours() + 1)
    // datetime-local wants 'YYYY-MM-DDTHH:mm'
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:00`
  }

  function handleEditorialApprove(publishNow: boolean) {
    if (!article) return
    const scheduledFor = publishNow
      ? new Date().toISOString()
      : new Date(scheduleDate).toISOString()
    workflowAction.mutate(
      { type: 'editorial_approve', id: article.id, by: currentUserSub, byName: user?.name, scheduledFor },
      {
        onSuccess: () => {
          setApproveDialogOpen(false)
          setScheduleDate('')
          toast.success(publishNow ? 'Approved — publishing now' : 'Approved — scheduled for publication')
        },
        onError: () => toast.error('Failed to record approval'),
      },
    )
  }

  function handleEditorialReject() {
    if (!article || !rejectNote.trim()) return
    workflowAction.mutate(
      { type: 'editorial_reject', id: article.id, by: currentUserSub, byName: user?.name, note: rejectNote.trim() },
      {
        onSuccess: () => {
          setRejectDialogOpen(false)
          setRejectNote('')
          toast.success('Rejected — returned to QC for revision')
        },
        onError: () => toast.error('Failed to record rejection'),
      },
    )
  }

  function handleHandoff(targetStage: 'qc_review' | 'layout_review') {
    if (!article) return
    workflowAction.mutate(
      { type: 'handoff', id: article.id, targetStage },
      {
        onSuccess: () =>
          toast.success(targetStage === 'layout_review' ? 'Passed to Layout' : 'Returned to QC'),
        onError: () => toast.error('Failed to update workflow'),
      },
    )
  }

  function handleReschedule() {
    if (!article || !rescheduleDate) return
    workflowAction.mutate(
      { type: 'reschedule', id: article.id, scheduledFor: new Date(rescheduleDate).toISOString() },
      {
        onSuccess: () => {
          setRescheduleDialogOpen(false)
          setRescheduleDate('')
          toast.success('Publication time updated')
        },
        onError: () => toast.error('Failed to update schedule'),
      },
    )
  }

  function handleUnschedule() {
    if (!article || !unscheduleNote.trim()) return
    workflowAction.mutate(
      { type: 'unschedule', id: article.id, by: currentUserSub, byName: user?.name, note: unscheduleNote.trim() },
      {
        onSuccess: () => {
          setUnscheduleDialogOpen(false)
          setUnscheduleNote('')
          toast.success('Unscheduled — returned to QC for revision')
        },
        onError: () => toast.error('Failed to unschedule'),
      },
    )
  }

  function handleSignoff() {
    if (!article) return
    const actionType = isQcHolder ? 'qc_signoff' : 'layout_signoff'
    const otherAlreadySigned = isQcHolder ? layoutSigned : qcSigned
    workflowAction.mutate(
      { type: actionType, id: article.id, by: currentUserSub, byName: user?.name },
      {
        onSuccess: () => {
          if (otherAlreadySigned) {
            toast.success('Signed off — sent to Editorial Approval')
          } else if (isQcHolder) {
            toast.success('Signed off — passed to Layout')
          } else {
            toast.success('Signed off — returned to QC')
          }
        },
        onError: () => toast.error('Failed to record sign-off'),
      },
    )
  }

  function handlePostPubApprove() {
    if (!article) return
    workflowAction.mutate(
      { type: 'post_pub_approve', id: article.id, by: currentUserSub, byName: user?.name },
      {
        onSuccess: () => {
          setPostPubApproveDialogOpen(false)
          toast.success('Approved & promoted to Data Repository')
        },
        onError: () => toast.error('Failed to approve — please try again'),
      },
    )
  }

  function handlePostPubRevision() {
    if (!article || !postPubRevisionNote.trim()) return
    workflowAction.mutate(
      { type: 'post_pub_revision', id: article.id, by: currentUserSub, byName: user?.name, note: postPubRevisionNote.trim() },
      {
        onSuccess: () => {
          setPostPubRevisionDialogOpen(false)
          setPostPubRevisionNote('')
          toast.success('Sent for revision — article remains live, QC notified')
        },
        onError: () => toast.error('Failed to request revision'),
      },
    )
  }

  function handlePostPubRecall() {
    if (!article || !postPubRecallNote.trim()) return
    workflowAction.mutate(
      { type: 'post_pub_recall', id: article.id, by: currentUserSub, byName: user?.name, note: postPubRecallNote.trim(), recallType: postPubRecallType },
      {
        onSuccess: () => {
          setPostPubRecallDialogOpen(false)
          setPostPubRecallNote('')
          setPostPubRecallType('substantive-recall')
          toast.success('Article recalled from publication')
        },
        onError: () => toast.error('Failed to recall article'),
      },
    )
  }

  function handleBlock() {
    if (!article || !blockAdditionalInfo.trim()) return
    blockArticle.mutate(
      { action: 'block', id: article.id, reason: blockReason, additionalInfo: blockAdditionalInfo.trim(), by: currentUserSub, byName: user?.name },
      {
        onSuccess: () => {
          setBlockDialogOpen(false)
          setBlockAdditionalInfo('')
          setBlockReason('waiting_for_author')
          toast.success('Article blocked')
        },
        onError: () => toast.error('Failed to block article'),
      },
    )
  }

  function handleUnblock() {
    if (!article) return
    blockArticle.mutate(
      { action: 'unblock', id: article.id, by: currentUserSub, byName: user?.name },
      {
        onSuccess: () => {
          setUnblockDialogOpen(false)
          toast.success('Article unblocked')
        },
        onError: () => toast.error('Failed to unblock article'),
      },
    )
  }

  const Icon = article?.productType === 'biographic' ? BookUser : FileText

  return (
    <>
      {/* Backdrop — sits below the sticky h-12 admin nav */}
      {open && (
        <div
          className="fixed inset-x-0 bottom-0 top-12 z-30 bg-black/20"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Shelf panel — starts below the sticky h-12 admin nav */}
      <div
        className="fixed right-0 bottom-0 top-12 z-40 flex w-[22rem] flex-col border-l border-line bg-canvas shadow-2xl transition-transform duration-200 ease-in-out"
        style={{ transform: open ? 'translateX(0)' : 'translateX(100%)' }}
        role="complementary"
        aria-label="Article detail shelf"
      >
        {article && (
          <>
            {/* ── Header ──────────────────────────────────────────────── */}
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-line px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-line bg-surface text-fg-secondary">
                    <Icon size={10} aria-hidden="true" />
                  </span>
                  <StageBadge stage={article.workflow.stage} />
                  {article.workflow.currentCycle > 1 && !cycle?.editorialApproval && (
                    <span className="rounded-full bg-red-3 px-1.5 py-0.5 text-[10px] font-bold text-red-11">
                      Cycle {article.workflow.currentCycle}
                    </span>
                  )}
                </div>
                <p className="line-clamp-2 text-[13px] font-semibold leading-snug text-fg">
                  {article.title}
                </p>
                {(article.documentId || article.classification) && (
                  <p className="mt-1 flex items-center gap-1.5 text-[10px] text-fg-secondary">
                    {article.documentId && (
                      <span className="font-mono">{article.documentId}</span>
                    )}
                    {article.documentId && article.classification && (
                      <span className="opacity-30">·</span>
                    )}
                    {article.classification && (
                      <span className="font-mono font-semibold">
                        ({[article.classification, ...(article.disseminationControls ?? [])].join('//')})
                      </span>
                    )}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="mt-0.5 shrink-0 rounded p-1 text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>

            {/* ── Quick actions bar ────────────────────────────────────── */}
            <div className="flex shrink-0 items-center justify-between border-b border-line px-4 py-2">
              <Link
                to={`/admin/articles/${article.id}`}
                className="flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
              >
                <ExternalLink size={11} aria-hidden="true" />
                Open article
              </Link>
              <div className="flex items-center gap-1">
                {article.workflow.activeBlock ? (
                  <button
                    type="button"
                    onClick={() => setUnblockDialogOpen(true)}
                    className="flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold text-amber-11 transition-colors hover:bg-fill"
                    aria-label="Unblock article"
                  >
                    <Ban size={11} aria-hidden="true" />
                    Unblock
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setBlockDialogOpen(true)}
                    className="flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
                    aria-label="Block article"
                  >
                    <Ban size={11} aria-hidden="true" />
                    Block
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onDeleteRequest(article)}
                  className="flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-error-fg"
                  aria-label="Delete draft"
                >
                  <Trash2 size={11} aria-hidden="true" />
                  Delete draft
                </button>
              </div>
            </div>

            {/* ── Blocked banner ────────────────────────────────────────── */}
            {article.workflow.activeBlock && (
              <div className="mx-4 mt-3 flex items-start gap-2 rounded-md border border-amber-6 bg-amber-2 px-3 py-2.5 text-[11px] leading-snug text-amber-11">
                <Ban size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <p className="font-semibold">
                    Blocked
                    {article.workflow.activeBlock.reason === 'waiting_for_author' ? ' — Waiting for Author' : ' — Other'}
                  </p>
                  <p className="mt-0.5 opacity-80">{article.workflow.activeBlock.additionalInfo}</p>
                  {article.workflow.activeBlock.blockedByName && (
                    <p className="mt-1 opacity-60">By {article.workflow.activeBlock.blockedByName}</p>
                  )}
                </div>
              </div>
            )}

            {/* ── Scrollable body ──────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto">

              {/* Workflow actions */}
              {hasWorkflowActions && (
                <div className="px-4 pt-4">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                    Actions
                  </p>
                  <div className="flex flex-col gap-2">

                    {/* Handoff */}
                    {actions.has('pass_to_layout') && (
                      <button
                        type="button"
                        onClick={() => handleHandoff('layout_review')}
                        disabled={workflowAction.isPending}
                        className="flex items-center justify-between rounded-md border border-line bg-surface px-3 py-2.5 text-[12px] font-semibold text-fg transition-colors hover:border-line-strong hover:bg-fill disabled:opacity-50"
                      >
                        <span>Pass to Layout</span>
                        <ArrowRight size={13} className="shrink-0 text-fg-secondary" aria-hidden="true" />
                      </button>
                    )}
                    {actions.has('return_to_qc') && (
                      <button
                        type="button"
                        onClick={() => handleHandoff('qc_review')}
                        disabled={workflowAction.isPending}
                        className="flex items-center justify-between rounded-md border border-line bg-surface px-3 py-2.5 text-[12px] font-semibold text-fg transition-colors hover:border-line-strong hover:bg-fill disabled:opacity-50"
                      >
                        <ArrowLeft size={13} className="shrink-0 text-fg-secondary" aria-hidden="true" />
                        <span>Return to QC</span>
                      </button>
                    )}

                    {/* QC sign-off */}
                    {actions.has('qc_signoff') && (
                      <button
                        type="button"
                        onClick={handleSignoff}
                        disabled={workflowAction.isPending}
                        className="flex items-center justify-between rounded-md border border-line bg-surface px-3 py-2.5 text-[12px] font-semibold text-fg transition-colors hover:border-line-strong hover:bg-fill disabled:opacity-50"
                      >
                        <span>Sign Off as QC</span>
                        {workflowAction.isPending
                          ? <Loader2 size={13} className="animate-spin shrink-0 text-fg-secondary" aria-hidden="true" />
                          : <PenLine size={13} className="shrink-0 text-fg-secondary" aria-hidden="true" />
                        }
                      </button>
                    )}
                    {isQcHolder && qcSigned && (
                      <div className="flex items-center gap-2 rounded-md bg-jade-2 px-3 py-2.5 text-[12px] font-semibold text-jade-11">
                        <Check size={13} className="shrink-0" aria-hidden="true" />
                        QC Signed Off
                      </div>
                    )}

                    {/* Layout sign-off */}
                    {actions.has('layout_signoff') && (
                      <button
                        type="button"
                        onClick={handleSignoff}
                        disabled={workflowAction.isPending}
                        className="flex items-center justify-between rounded-md border border-line bg-surface px-3 py-2.5 text-[12px] font-semibold text-fg transition-colors hover:border-line-strong hover:bg-fill disabled:opacity-50"
                      >
                        <span>Sign Off as Layout</span>
                        {workflowAction.isPending
                          ? <Loader2 size={13} className="animate-spin shrink-0 text-fg-secondary" aria-hidden="true" />
                          : <PenLine size={13} className="shrink-0 text-fg-secondary" aria-hidden="true" />
                        }
                      </button>
                    )}
                    {isLayoutHolder && layoutSigned && (
                      <div className="flex items-center gap-2 rounded-md bg-jade-2 px-3 py-2.5 text-[12px] font-semibold text-jade-11">
                        <Check size={13} className="shrink-0" aria-hidden="true" />
                        Layout Signed Off
                      </div>
                    )}

                    {/* Editorial approval or rejection */}
                    {actions.has('editorial_approve') && actions.has('editorial_reject') && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setScheduleDate(defaultScheduleDatetime())
                            setApproveDialogOpen(true)
                          }}
                          disabled={workflowAction.isPending}
                          className="flex items-center justify-between rounded-md bg-jade-9 px-3 py-2.5 text-[12px] font-semibold text-white transition-opacity disabled:opacity-50"
                        >
                          <span>Approve&hellip;</span>
                          <CalendarCheck size={13} className="shrink-0" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setRejectDialogOpen(true)}
                          disabled={workflowAction.isPending}
                          className="flex items-center justify-between rounded-md border border-line bg-surface px-3 py-2.5 text-[12px] font-semibold text-red-11 transition-colors hover:border-line-strong hover:bg-fill disabled:opacity-50"
                        >
                          <span>Reject&hellip;</span>
                          <XCircle size={13} className="shrink-0" aria-hidden="true" />
                        </button>
                      </>
                    )}
                    {isEditorialHolder && editorialApproved && (
                      <div className="flex items-center gap-2 rounded-md bg-jade-2 px-3 py-2.5 text-[12px] font-semibold text-jade-11">
                        <Check size={13} className="shrink-0" aria-hidden="true" />
                        Editorial Approved
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Separator between actions and status — inset so it doesn't bifurcate the panel */}
              {hasWorkflowActions && (
                <div className="px-4 pt-4">
                  <hr className="border-t border-line" style={{ marginInline: '1rem' }} />
                </div>
              )}

              {/* Scheduled time + editorial controls — for editorial users on scheduled articles */}
              {stage === 'scheduled' && actions.has('reschedule') && actions.has('unschedule') && article.workflow.scheduledFor && (
                <div className="px-4 pt-4">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                    Publication
                  </p>
                  <div className="mb-3 flex items-center gap-2 rounded-md bg-teal-2 px-3 py-3 text-teal-11">
                    <CalendarCheck size={15} className="shrink-0" aria-hidden="true" />
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">Scheduled for</p>
                      <p className="text-[13px] font-bold">
                        {new Date(article.workflow.scheduledFor).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setRescheduleDate(article.workflow.scheduledFor
                          ? article.workflow.scheduledFor.slice(0, 16)
                          : defaultScheduleDatetime())
                        setRescheduleDialogOpen(true)
                      }}
                      disabled={workflowAction.isPending}
                      className="flex items-center justify-between rounded-md border border-line bg-surface px-3 py-2.5 text-[12px] font-semibold text-fg transition-colors hover:border-line-strong hover:bg-fill disabled:opacity-50"
                    >
                      <span>Change schedule&hellip;</span>
                      <CalendarClock size={13} className="shrink-0 text-fg-secondary" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setUnscheduleDialogOpen(true)}
                      disabled={workflowAction.isPending}
                      className="flex items-center justify-between rounded-md border border-line bg-surface px-3 py-2.5 text-[12px] font-semibold text-red-11 transition-colors hover:border-line-strong hover:bg-fill disabled:opacity-50"
                    >
                      <span>Unschedule &amp; return to QC&hellip;</span>
                      <XCircle size={13} className="shrink-0" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )}

              {/* Post-pub review controls — for the assigned post-pub reviewer */}
              {stage === 'post_pub_review' && actions.has('post_pub_finalize') && actions.has('post_pub_revision') && actions.has('post_pub_recall') && (
                <div className="px-4 pt-4">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                    Post-Pub Review
                  </p>
                  <div
                    className="mb-3 flex items-start gap-2 rounded-md bg-warning-fill px-3 py-2.5 text-[11px] leading-snug text-warning-fg"
                  >
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
                    <p>This article is live. Review actions take immediate effect.</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => setPostPubApproveDialogOpen(true)}
                      disabled={workflowAction.isPending}
                      className="flex items-center justify-between rounded-md border border-line bg-surface px-3 py-2.5 text-[12px] font-semibold text-fg transition-colors hover:border-line-strong hover:bg-fill disabled:opacity-50"
                    >
                      <span>Finalize &amp; Archive</span>
                      <Check size={13} className="shrink-0 text-fg-secondary" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPostPubRevisionDialogOpen(true)}
                      disabled={workflowAction.isPending || !canIssueRevision}
                      title={!canIssueRevision ? 'You do not have permission to issue revisions' : undefined}
                      className="flex items-center justify-between rounded-md border border-line bg-surface px-3 py-2.5 text-[12px] font-semibold text-fg transition-colors hover:border-line-strong hover:bg-fill disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <span>Return to QC for Revision&hellip;</span>
                      <RotateCcw size={13} className="shrink-0 text-fg-secondary" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPostPubRecallDialogOpen(true)}
                      disabled={workflowAction.isPending}
                      className="flex items-center justify-between rounded-md border border-line bg-surface px-3 py-2.5 text-[12px] font-semibold text-red-11 transition-colors hover:border-line-strong hover:bg-fill disabled:opacity-50"
                    >
                      <span>Recall&hellip;</span>
                      <Ban size={13} className="shrink-0" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )}

              {/* Rejection note from previous cycle — hidden once current cycle is approved */}
              {article.workflow.currentCycle > 1 && !cycle?.editorialApproval && (() => {
                const prev = article.workflow.cycles?.[article.workflow.currentCycle - 2]
                const note = prev?.closedBy?.note
                if (!note) return null
                return (
                  <div className="mx-4 mt-4 flex items-start gap-2 rounded-md bg-error-fill px-3 py-2.5 text-[11px] leading-snug text-error-fg">
                    <AlertTriangle size={11} className="mt-0.5 shrink-0" aria-hidden="true" />
                    <div>
                      <p className="font-semibold">Rejected by Editorial</p>
                      <p className="mt-0.5 opacity-80">{note}</p>
                    </div>
                  </div>
                )
              })()}

              {/* Sign-off status */}
              <div className="px-4 pt-4">
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                  Sign-offs
                </p>
                <div className="divide-y divide-line rounded-md border border-line px-3">
                  <SignoffRow label="QC Review"           accentVar="--amber-9"  signoff={cycle?.qcSignoff} />
                  <SignoffRow label="Layout"              accentVar="--blue-9"   signoff={cycle?.layoutSignoff} />
                  <SignoffRow label="Editorial Approval"  accentVar="--violet-9" signoff={cycle?.editorialApproval} />
                </div>
              </div>

              {/* Assignees */}
              <div className="px-4 pt-4 pb-4">
                <div className="mb-1.5 flex items-center justify-between">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                    Assignees
                  </p>
                  <button
                    type="button"
                    onClick={() => onAssignRequest(article)}
                    className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
                  >
                    <Users size={9} aria-hidden="true" />
                    Reassign
                  </button>
                </div>
                <div className="divide-y divide-line rounded-md border border-line px-3">
                  {[
                    { role: 'QC',        sub: article.workflow.qcAssignedTo,        accentVar: '--amber-9' },
                    { role: 'Layout',    sub: article.workflow.layoutAssignedTo,    accentVar: '--blue-9' },
                    { role: 'Editorial', sub: article.workflow.editorialAssignedTo, accentVar: '--violet-9' },
                    { role: 'Post-Pub',  sub: article.workflow.postPubAssignedTo,   accentVar: '--amber-9' },
                  ].map(({ role, sub, accentVar }) => (
                    <div key={role} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: `var(${accentVar})` }}
                          aria-hidden="true"
                        />
                        <span className="text-[11px] text-fg-secondary">{role}</span>
                      </div>
                      {sub ? (
                        <span className="text-[11px] font-medium text-fg">
                          {sub === currentUserSub ? 'You' : sub}
                        </span>
                      ) : (
                        <span className="text-[10px] text-amber-11">Unassigned</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Reschedule dialog ────────────────────────────────── */}
            <Dialog.Root
              open={rescheduleDialogOpen}
              onOpenChange={(v) => {
                setRescheduleDialogOpen(v)
                if (!v) setRescheduleDate('')
              }}
            >
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
                <Dialog.Content
                  className="fixed left-1/2 top-1/2 z-50 w-96 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
                  aria-describedby="reschedule-dialog-desc"
                >
                  <Dialog.Title className="text-sm font-semibold text-fg">
                    Change publication schedule
                  </Dialog.Title>
                  <Dialog.Description id="reschedule-dialog-desc" className="mt-1 text-xs text-fg-secondary">
                    Set a new publication time for this article.
                  </Dialog.Description>
                  <div className="mt-4">
                    <label className="block text-[11px] font-semibold text-fg-secondary" htmlFor="reschedule-datetime">
                      New publication date &amp; time
                    </label>
                    <input
                      id="reschedule-datetime"
                      type="datetime-local"
                      className="mt-1.5 w-full rounded border border-line bg-surface px-3 py-2 text-[12px] text-fg focus:border-line-strong focus:outline-none"
                      value={rescheduleDate}
                      onChange={(e) => setRescheduleDate(e.target.value)}
                    />
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                      >
                        Cancel
                      </button>
                    </Dialog.Close>
                    <button
                      type="button"
                      onClick={handleReschedule}
                      disabled={!rescheduleDate || workflowAction.isPending}
                      className="rounded bg-teal-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
                    >
                      {workflowAction.isPending ? 'Saving…' : 'Update schedule'}
                    </button>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

            {/* ── Unschedule dialog ────────────────────────────────── */}
            <Dialog.Root
              open={unscheduleDialogOpen}
              onOpenChange={(v) => {
                setUnscheduleDialogOpen(v)
                if (!v) setUnscheduleNote('')
              }}
            >
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
                <Dialog.Content
                  className="fixed left-1/2 top-1/2 z-50 w-96 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
                  aria-describedby="unschedule-dialog-desc"
                >
                  <Dialog.Title className="text-sm font-semibold text-fg">
                    Unschedule &amp; return to QC
                  </Dialog.Title>
                  <Dialog.Description id="unschedule-dialog-desc" className="mt-1 text-xs text-fg-secondary">
                    Explain what needs to change. QC will see this note when they pick up the article.
                  </Dialog.Description>
                  <textarea
                    className="mt-3 w-full rounded border border-line bg-surface px-3 py-2 text-[12px] text-fg placeholder:text-fg-secondary focus:border-line-strong focus:outline-none"
                    rows={4}
                    placeholder="What needs to change before this article is ready for publication?"
                    value={unscheduleNote}
                    onChange={(e) => setUnscheduleNote(e.target.value)}
                    autoFocus
                  />
                  <div className="mt-4 flex justify-end gap-2">
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                      >
                        Cancel
                      </button>
                    </Dialog.Close>
                    <button
                      type="button"
                      onClick={handleUnschedule}
                      disabled={!unscheduleNote.trim() || workflowAction.isPending}
                      className="rounded bg-red-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
                    >
                      {workflowAction.isPending ? 'Sending…' : 'Unschedule'}
                    </button>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

            {/* ── Approval / schedule dialog ───────────────────────── */}
            <Dialog.Root
              open={approveDialogOpen}
              onOpenChange={(v) => {
                setApproveDialogOpen(v)
                if (!v) setScheduleDate('')
              }}
            >
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
                <Dialog.Content
                  className="fixed left-1/2 top-1/2 z-50 w-96 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
                  aria-describedby="approve-dialog-desc"
                >
                  <Dialog.Title className="text-sm font-semibold text-fg">
                    Approve for publication
                  </Dialog.Title>
                  <Dialog.Description id="approve-dialog-desc" className="mt-1 text-xs text-fg-secondary">
                    Schedule a future publication time, or publish the article immediately.
                  </Dialog.Description>

                  <div className="mt-4">
                    <label className="block text-[11px] font-semibold text-fg-secondary" htmlFor="schedule-datetime">
                      Publication date &amp; time
                    </label>
                    <input
                      id="schedule-datetime"
                      type="datetime-local"
                      className="mt-1.5 w-full rounded border border-line bg-surface px-3 py-2 text-[12px] text-fg focus:border-line-strong focus:outline-none"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-2">
                    {/* Publish now — left side */}
                    <button
                      type="button"
                      onClick={() => handleEditorialApprove(true)}
                      disabled={workflowAction.isPending}
                      className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill disabled:opacity-40"
                    >
                      {workflowAction.isPending
                        ? <Loader2 size={11} className="animate-spin shrink-0" aria-hidden="true" />
                        : <Check size={11} className="shrink-0" aria-hidden="true" />
                      }
                      Publish now
                    </button>

                    {/* Cancel + Schedule — right side */}
                    <div className="flex gap-2">
                      <Dialog.Close asChild>
                        <button
                          type="button"
                          className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                        >
                          Cancel
                        </button>
                      </Dialog.Close>
                      <button
                        type="button"
                        onClick={() => handleEditorialApprove(false)}
                        disabled={!scheduleDate || workflowAction.isPending}
                        className="rounded bg-jade-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
                      >
                        {workflowAction.isPending ? 'Scheduling…' : 'Schedule'}
                      </button>
                    </div>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

            {/* ── Rejection note dialog ────────────────────────────── */}
            <Dialog.Root
              open={rejectDialogOpen}
              onOpenChange={(v) => {
                setRejectDialogOpen(v)
                if (!v) setRejectNote('')
              }}
            >
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
                <Dialog.Content
                  className="fixed left-1/2 top-1/2 z-50 w-96 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
                  aria-describedby="reject-dialog-desc"
                >
                  <Dialog.Title className="text-sm font-semibold text-fg">
                    Reject &amp; return to QC
                  </Dialog.Title>
                  <Dialog.Description id="reject-dialog-desc" className="mt-1 text-xs text-fg-secondary">
                    Describe what needs to change. This note will be shown to QC when they pick up the article.
                  </Dialog.Description>
                  <textarea
                    className="mt-3 w-full rounded border border-line bg-surface px-3 py-2 text-[12px] text-fg placeholder:text-fg-secondary focus:border-line-strong focus:outline-none"
                    rows={4}
                    placeholder="What needs to change before this article is ready for publication?"
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    autoFocus
                  />
                  {hasUnclassifiedBlocks && (
                    <p className="mt-2 flex items-center gap-1.5 text-[11px] text-error-fg">
                      <AlertTriangle size={11} aria-hidden="true" />
                      All article blocks must have portion marks before rejecting.
                    </p>
                  )}
                  <div className="mt-4 flex justify-end gap-2">
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                      >
                        Cancel
                      </button>
                    </Dialog.Close>
                    <button
                      type="button"
                      onClick={handleEditorialReject}
                      disabled={!rejectNote.trim() || hasUnclassifiedBlocks || workflowAction.isPending}
                      className="rounded bg-red-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
                    >
                      {workflowAction.isPending ? 'Sending…' : 'Send back to QC'}
                    </button>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
            {/* ── Post-pub approve confirmation ──────────────────────── */}
            <Dialog.Root open={postPubApproveDialogOpen} onOpenChange={setPostPubApproveDialogOpen}>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
                <Dialog.Content
                  className="fixed left-1/2 top-1/2 z-50 w-96 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
                  aria-describedby="post-pub-approve-desc"
                >
                  <Dialog.Title className="text-sm font-semibold text-fg">
                    Finalize &amp; Archive
                  </Dialog.Title>
                  <Dialog.Description id="post-pub-approve-desc" className="mt-1 text-xs text-fg-secondary">
                    This marks the post-publication review as complete. The article will be submitted to the Data Repository of Record and indexed for search.
                  </Dialog.Description>
                  <div className="mt-4 flex justify-end gap-2">
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                      >
                        Cancel
                      </button>
                    </Dialog.Close>
                    <button
                      type="button"
                      onClick={handlePostPubApprove}
                      disabled={workflowAction.isPending}
                      className="rounded bg-jade-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
                    >
                      {workflowAction.isPending ? 'Archiving…' : 'Finalize & Archive'}
                    </button>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

            {/* ── Post-pub revision dialog ─────────────────────────── */}
            <Dialog.Root
              open={postPubRevisionDialogOpen}
              onOpenChange={(v) => {
                setPostPubRevisionDialogOpen(v)
                if (!v) setPostPubRevisionNote('')
              }}
            >
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
                <Dialog.Content
                  className="fixed left-1/2 top-1/2 z-50 w-96 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
                  aria-describedby="post-pub-revision-desc"
                >
                  <Dialog.Title className="text-sm font-semibold text-fg">
                    Return to QC for Revision
                  </Dialog.Title>
                  <Dialog.Description id="post-pub-revision-desc" className="mt-1 text-xs text-fg-secondary">
                    This will return the article to Quality Control. To make minor edits, or submit a revision yourself, open the article in the editor and use the Revisions button.
                  </Dialog.Description>
                  <textarea
                    className="mt-3 w-full rounded border border-line bg-surface px-3 py-2 text-[12px] text-fg placeholder:text-fg-secondary focus:border-line-strong focus:outline-none"
                    rows={4}
                    placeholder="What needs to change?"
                    value={postPubRevisionNote}
                    onChange={(e) => setPostPubRevisionNote(e.target.value)}
                    autoFocus
                  />
                  <div className="mt-4 flex justify-end gap-2">
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                      >
                        Cancel
                      </button>
                    </Dialog.Close>
                    <button
                      type="button"
                      onClick={handlePostPubRevision}
                      disabled={!postPubRevisionNote.trim() || workflowAction.isPending}
                      className="rounded bg-amber-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
                    >
                      {workflowAction.isPending ? 'Sending…' : 'Return to QC'}
                    </button>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

            {/* ── Post-pub recall dialog ────────────────────────────── */}
            <Dialog.Root
              open={postPubRecallDialogOpen}
              onOpenChange={(v) => {
                setPostPubRecallDialogOpen(v)
                if (!v) {
                  setPostPubRecallNote('')
                  setPostPubRecallType('substantive-recall')
                }
              }}
            >
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
                <Dialog.Content
                  className="fixed left-1/2 top-1/2 z-50 w-96 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
                  aria-describedby="post-pub-recall-desc"
                >
                  <Dialog.Title className="text-sm font-semibold text-fg">
                    Recall article
                  </Dialog.Title>
                  <Dialog.Description id="post-pub-recall-desc" className="mt-1 text-xs text-fg-secondary">
                    The article will be immediately hidden from readers. Provide a recall type and reason.
                  </Dialog.Description>
                  <div className="mt-3">
                    <label className="block text-[11px] font-semibold text-fg-secondary" htmlFor="recall-type">
                      Recall type
                    </label>
                    <select
                      id="recall-type"
                      value={postPubRecallType}
                      onChange={(e) => setPostPubRecallType(e.target.value as typeof postPubRecallType)}
                      className="mt-1.5 w-full rounded border border-line bg-surface px-3 py-2 text-[12px] text-fg focus:border-line-strong focus:outline-none"
                    >
                      <option value="substantive-recall">Substantive Recall</option>
                      <option value="administrative-recall">Administrative Recall</option>
                      <option value="compliance-recall">Compliance Recall</option>
                    </select>
                  </div>
                  <textarea
                    className="mt-3 w-full rounded border border-line bg-surface px-3 py-2 text-[12px] text-fg placeholder:text-fg-secondary focus:border-line-strong focus:outline-none"
                    rows={3}
                    placeholder="Reason for recall"
                    value={postPubRecallNote}
                    onChange={(e) => setPostPubRecallNote(e.target.value)}
                    autoFocus
                  />
                  <div className="mt-4 flex justify-end gap-2">
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                      >
                        Cancel
                      </button>
                    </Dialog.Close>
                    <button
                      type="button"
                      onClick={handlePostPubRecall}
                      disabled={!postPubRecallNote.trim() || workflowAction.isPending}
                      className="rounded bg-red-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
                    >
                      {workflowAction.isPending ? 'Recalling…' : 'Recall Article'}
                    </button>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

            {/* ── Unblock confirmation dialog ───────────────────────── */}
            <Dialog.Root open={unblockDialogOpen} onOpenChange={setUnblockDialogOpen}>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
                <Dialog.Content
                  className="fixed left-1/2 top-1/2 z-50 w-80 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
                  aria-describedby="unblock-dialog-desc"
                >
                  <Dialog.Title className="text-sm font-semibold text-fg">
                    Unblock article
                  </Dialog.Title>
                  <Dialog.Description id="unblock-dialog-desc" className="mt-1 text-xs text-fg-secondary">
                    This will resume the article in its current workflow stage.
                  </Dialog.Description>
                  {article?.workflow.activeBlock && (
                    <div className="mt-3 rounded-md border border-amber-6 bg-amber-2 px-3 py-2.5 text-[11px] text-amber-11">
                      <p className="font-semibold">
                        {article.workflow.activeBlock.reason === 'waiting_for_author' ? 'Waiting for Author' : 'Other'}
                      </p>
                      <p className="mt-0.5 opacity-80">{article.workflow.activeBlock.additionalInfo}</p>
                    </div>
                  )}
                  <div className="mt-4 flex justify-end gap-2">
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                      >
                        Cancel
                      </button>
                    </Dialog.Close>
                    <button
                      type="button"
                      onClick={handleUnblock}
                      disabled={blockArticle.isPending}
                      className="rounded bg-amber-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
                    >
                      {blockArticle.isPending ? 'Unblocking…' : 'Unblock article'}
                    </button>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

            {/* ── Block article dialog ──────────────────────────────── */}
            <Dialog.Root
              open={blockDialogOpen}
              onOpenChange={(v) => {
                setBlockDialogOpen(v)
                if (!v) {
                  setBlockAdditionalInfo('')
                  setBlockReason('waiting_for_author')
                }
              }}
            >
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
                <Dialog.Content
                  className="fixed left-1/2 top-1/2 z-50 w-96 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
                  aria-describedby="block-dialog-desc"
                >
                  <Dialog.Title className="text-sm font-semibold text-fg">
                    Block article
                  </Dialog.Title>
                  <Dialog.Description id="block-dialog-desc" className="mt-1 text-xs text-fg-secondary">
                    Blocking pauses this article in its current workflow stage until it is unblocked.
                  </Dialog.Description>
                  <div className="mt-4">
                    <label className="block text-[11px] font-semibold text-fg-secondary" htmlFor="block-reason">
                      Reason
                    </label>
                    <select
                      id="block-reason"
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value as WireBlockReason)}
                      className="mt-1.5 w-full rounded border border-line bg-surface px-3 py-2 text-[12px] text-fg focus:border-line-strong focus:outline-none"
                    >
                      <option value="waiting_for_author">Waiting for Author</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="mt-3">
                    <label className="block text-[11px] font-semibold text-fg-secondary" htmlFor="block-info">
                      Additional information
                    </label>
                    <textarea
                      id="block-info"
                      className="mt-1.5 w-full rounded border border-line bg-surface px-3 py-2 text-[12px] text-fg placeholder:text-fg-secondary focus:border-line-strong focus:outline-none"
                      rows={3}
                      placeholder="Provide details about why this article is being blocked…"
                      value={blockAdditionalInfo}
                      onChange={(e) => setBlockAdditionalInfo(e.target.value)}
                      autoFocus
                    />
                  </div>
                  {hasUnclassifiedBlocks && (
                    <p className="mt-2 flex items-center gap-1.5 text-[11px] text-error-fg">
                      <AlertTriangle size={11} aria-hidden="true" />
                      All article blocks must have portion marks before blocking.
                    </p>
                  )}
                  <div className="mt-4 flex justify-end gap-2">
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
                      >
                        Cancel
                      </button>
                    </Dialog.Close>
                    <button
                      type="button"
                      onClick={handleBlock}
                      disabled={!blockAdditionalInfo.trim() || hasUnclassifiedBlocks || blockArticle.isPending}
                      className="rounded bg-amber-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
                    >
                      {blockArticle.isPending ? 'Blocking…' : 'Block article'}
                    </button>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

            {/* ── Footer ──────────────────────────────────────────────── */}
            {!alreadyMine && (
              <div className="flex shrink-0 items-center border-t border-line px-4 py-3">
                <button
                  type="button"
                  onClick={() => onReassignToMe(article)}
                  className="flex items-center gap-1 rounded px-2 py-1.5 text-[11px] font-semibold text-blue-11 transition-colors hover:bg-fill"
                >
                  <UserPlus size={11} aria-hidden="true" />
                  Assign to me
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
